import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import {
  Plus, Loader2, RefreshCw, Search, ChevronRight, X as XIcon, Trash2, GripVertical,
  ClipboardList, Check, AlertTriangle, Copy, PencilRuler, Sparkles, Upload, Wand2,
  Eye, Rocket, History, FileEdit, ArrowLeft,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  listDrafts, saveDraft, deleteDraft,
  listVersions, nextVersion, recordPublish,
  attachVersionStamp, stripVersionStamp, parseInlineVersion,
} from "@/lib/templateVersioning";

/**
 * InspectionBuilder — 1:1 clone of the mobile app's Expert Builder.
 *
 * Backend contract (production Pit Link):
 *   GET    /api/inspection-templates
 *   POST   /api/admin/inspection-templates
 *   PATCH  /api/admin/inspection-templates/{tid}
 *   DELETE /api/admin/inspection-templates/{tid}
 *   POST   /api/admin/inspection-templates/{tid}/duplicate    (server-side clone)
 *
 * Expert (AI) builder:
 *   POST /api/admin/builder/generate            body { instruction, context? }
 *   POST /api/admin/builder/generate-from-file  (multipart)
 *   POST /api/admin/builder/save                body { template, reference_id? }
 */
// Josh (Feb 9): "change all day it says SMU but that needs to be numbers not
// okay and that". IDs are the mobile-app contract and MUST NOT change — the
// app agent detects `kind` = "number" / "text" / "photo" / "check" and
// renders the right UI. Labels are portal-only and are ordered so the four
// Josh cares about most (Status / Numeric / Text / Photo) sit at the top.
const ITEM_KINDS = [
  { id: "check",     label: "Status · OK / Issue / N/A",     hint: "Rugged three-button choice — this is the default." },
  { id: "number",    label: "Numeric · number pad",          hint: "For SMU, hours, pressures, temperatures, percentages." },
  { id: "text",      label: "Text · short note",             hint: "Free-text comment (single line)." },
  { id: "photo",     label: "Photo · required capture",      hint: "Dedicated camera button on the tablet." },
  { id: "select",    label: "Dropdown · pick one option",    hint: "Owner-defined options list. Renders as a pill picker on the tablet." },
  { id: "percent",   label: "Percent (0–100)",               hint: "Slider or numeric entry capped 0–100." },
  { id: "rating",    label: "Rating (1–5)",                  hint: "Star rating on the mobile app." },
  { id: "signature", label: "Signature capture",             hint: "Draw-with-finger signature panel." },
];
const CADENCES = ["prestart", "daily", "weekly", "monthly", "shutdown", "ad-hoc"];

export default function InspectionBuilder() {
  const [templates, setTemplates] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | {template}
  const [expert, setExpert] = useState(false);

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/inspection-templates");
      const arr = Array.isArray(data) ? data : data?.items || data?.results || data?.data || [];
      setTemplates(arr);
    } catch (ex) {
      setErr(apiError(ex));
      setTemplates([]);
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const list = templates || [];
    if (!q) return list;
    const s = q.toLowerCase();
    return list.filter((t) =>
      `${t.name || ""} ${t.machine_type || ""} ${t.cadence || ""} ${t.applies_to || ""}`.toLowerCase().includes(s)
    );
  }, [templates, q]);

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/inspection-templates/${t.id || t._id}`);
      reload();
    } catch (ex) {
      alert(apiError(ex));
    }
  };

  const duplicate = async (t) => {
    try {
      await api.post(`/admin/inspection-templates/${t.id || t._id}/duplicate`);
      reload();
    } catch (ex) {
      alert(apiError(ex));
    }
  };

  return (
    <div className="space-y-5" data-testid="inspection-builder">
      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <div className="flex-1 panel-flat px-3 flex items-center gap-2">
          <Search className="w-4 h-4 text-muted shrink-0" />
          <input
            className="w-full py-3 bg-transparent outline-none text-base"
            placeholder="Search inspection templates…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="ib-search"
          />
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy} data-testid="ib-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          <button className="btn btn-sm" onClick={() => setExpert(true)} data-testid="ib-expert">
            <Wand2 className="w-4 h-4" /> Expert Builder
          </button>
          <button className="btn btn-primary" onClick={() => setEditing({ template: null })} data-testid="ib-new">
            <Plus className="w-4 h-4" /> New Template
          </button>
        </div>
      </div>

      {err && (
        <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>
      )}

      {templates === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading templates…
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted">
          <PencilRuler className="w-10 h-10 mx-auto mb-3 text-muted" />
          <div className="font-display font-black text-xl">No inspection templates yet.</div>
          <div className="text-sm mt-1">Tap <span className="text-white font-bold">New Template</span> to build one, or <span className="text-white font-bold">Expert Builder</span> to generate from a prompt or PDF.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {/* Owner-only local drafts — appear as amber cards above the live
              templates so it's obvious which are unpublished. */}
          {listDrafts().map((d) => {
            const secCount = (d.sections || []).length;
            const itemCount = (d.sections || []).reduce((acc, s) => acc + (s.items || []).length, 0);
            return (
              <div key={d.draft_id} className="panel p-5 flex items-center gap-4" data-testid={`ib-draft-row-${d.draft_id}`}
                   style={{ border: "1px dashed rgba(245,130,32,0.5)" }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                     style={{ background: "rgba(245,130,32,0.10)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                  <FileEdit className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-black text-lg truncate">{d.name || "Untitled draft"}</div>
                    <span
                      className="pill !text-[10px] !py-0.5 !px-2 font-black uppercase tracking-widest"
                      style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}
                    >
                      Draft
                    </span>
                  </div>
                  <div className="text-xs text-muted font-mono-tight mt-0.5 truncate">
                    {d.machine_type || "any machine"}{d.cadence ? ` · ${d.cadence}` : ""} · {secCount} section{secCount === 1 ? "" : "s"} · {itemCount} item{itemCount === 1 ? "" : "s"} · saved {d.saved_at ? new Date(d.saved_at).toLocaleString() : "—"}
                  </div>
                </div>
                <button
                  className="btn btn-sm"
                  onClick={() => setEditing({ template: d })}
                  data-testid={`ib-draft-edit-${d.draft_id}`}
                >
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  className="btn btn-sm btn-ghost text-red-400 hover:text-red-300"
                  onClick={() => { deleteDraft(d.draft_id); reload(); }}
                  title="Discard draft"
                  data-testid={`ib-draft-del-${d.draft_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
          {filtered.map((t) => {
            const secCount = (t.sections || []).length;
            const itemCount = (t.sections || []).reduce((acc, s) => acc + (s.items || []).length, 0);
            const tid = t.id || t._id;
            const inline = parseInlineVersion(t.description);
            return (
              <div key={tid} className="panel p-5 flex items-center gap-4" data-testid={`ib-row-${tid}`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                     style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display font-black text-lg truncate">{t.name || "Untitled template"}</div>
                    {inline?.version && (
                      <span
                        className="pill !text-[10px] !py-0.5 !px-2 font-black uppercase tracking-widest"
                        style={{ background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)" }}
                        title={`Published by ${inline.published_by || "—"} on ${inline.published_at || "—"}`}
                        data-testid={`ib-version-pill-${tid}`}
                      >
                        Live · v{inline.version}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted font-mono-tight mt-0.5 truncate">
                    {t.machine_type || "any machine"}
                    {t.cadence && <> · {t.cadence}</>}
                    {t.require_smu && <> · SMU required</>}
                    {" "}· {secCount} section{secCount === 1 ? "" : "s"} · {itemCount} item{itemCount === 1 ? "" : "s"}
                  </div>
                </div>
                <button className="btn btn-sm btn-ghost" onClick={() => duplicate(t)} data-testid={`ib-dup-${tid}`}>
                  <Copy className="w-4 h-4" /> Duplicate
                </button>
                <button className="btn btn-sm" onClick={() => setEditing({ template: t })} data-testid={`ib-edit-${tid}`}>
                  Edit <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  className="btn btn-sm btn-ghost text-red-400 hover:text-red-300"
                  onClick={() => remove(t)}
                  title="Delete template"
                  data-testid={`ib-del-${tid}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <BuilderModal
          initial={editing.template}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
      {expert && (
        <ExpertBuilderModal
          onClose={() => setExpert(false)}
          onGenerated={(tpl) => { setExpert(false); setEditing({ template: tpl }); }}
        />
      )}
    </div>
  );
}

/* ---------------- Builder modal (manual create/edit) ---------------- */
function BuilderModal({ initial, onClose, onSaved }) {
  const { user } = useAuth();
  const [name, setName] = useState(initial?.name || "");
  const [machineType, setMachineType] = useState(initial?.machine_type || "");
  const [cadence, setCadence] = useState(initial?.cadence || "");
  const [description, setDescription] = useState(stripVersionStamp(initial?.description || ""));
  const [appliesTo, setAppliesTo] = useState(
    Array.isArray(initial?.applies_to)
      ? initial.applies_to.join(", ")
      : (initial?.applies_to || "")
  );
  const [requireSmu, setRequireSmu] = useState(!!initial?.require_smu);
  const [sections, setSections] = useState(
    (initial?.sections && initial.sections.length
      ? initial.sections
      : [{ title: "General", items: [{ label: "Overall condition", kind: "check", required: true }] }]
    ).map(normSection)
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [assetTypes, setAssetTypes] = useState([]);

  // Load the real asset categories logged on the system (LV, Hydraulic
  // Excavator, Track Dozer, …) so Josh can PICK from what's already in the
  // fleet instead of hand-typing. Josh (Feb 26): "make sure that you select
  // a categoty say LV or ex3600 or what every has been logged on the system".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/asset-types");
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : data?.items || [];
        setAssetTypes(arr.map((x) => (typeof x === "string" ? x : (x?.name || x?.id))).filter(Boolean));
      } catch {
        // Fallback — hardcode the common ones so the picker is never empty.
        setAssetTypes(["Hydraulic Excavator", "Dump Truck", "Track Dozer", "Wheel Loader", "Drill", "Grader", "Water Cart", "LV"]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isEdit = Boolean(initial?.id || initial?._id);
  const isDraft = Boolean(initial?.is_draft);           // came from localStorage
  const draftId = initial?.draft_id || null;
  const templateId = initial?.id || initial?._id || null;

  // Version metadata already stamped into `description` on the live record.
  const currentVersion = parseInlineVersion(initial?.description);
  const nextV = templateId ? nextVersion(templateId) : 1;
  const versionRows = templateId ? listVersions(templateId) : [];

  const addSection = () =>
    setSections((s) => [...s, { title: `Section ${s.length + 1}`, items: [{ label: "New item", kind: "check" }] }]);
  const removeSection = (i) => setSections((s) => s.filter((_, k) => k !== i));
  const updateSection = (i, patch) =>
    setSections((s) => s.map((sec, k) => (k === i ? { ...sec, ...patch } : sec)));
  const addItem = (si) =>
    setSections((s) => s.map((sec, k) => k === si ? { ...sec, items: [...sec.items, { label: "New item", kind: "check" }] } : sec));
  const removeItem = (si, ii) =>
    setSections((s) => s.map((sec, k) => k === si ? { ...sec, items: sec.items.filter((_, j) => j !== ii) } : sec));
  // Auto-suggest Numeric when the item label smells like a measurement.
  // Josh (Feb 9): "change all day it says SMU but that needs to be numbers
  // not okay and that". The Expert Builder AI defaults every item to
  // `kind: "check"`; this nudge flips reading-type items to `number` the
  // moment Josh types (or accepts) a numeric-sounding label — without
  // stomping on an explicit choice he already made. Only fires when the
  // item is still on the default `check`.
  const NUMERIC_PATTERN = /\b(smu|s\.m\.u|meter|reading|hours?|hrs?|pressure|temperature|temp|psi|bar|kpa|kwh|amps?|volts?|litres?|liters?|kg|degrees?|deg|rpm|torque|%|percent)\b/i;
  const updateItem = (si, ii, patch) =>
    setSections((s) => s.map((sec, k) => {
      if (k !== si) return sec;
      return {
        ...sec,
        items: sec.items.map((it, j) => {
          if (j !== ii) return it;
          const merged = { ...it, ...patch };
          if (
            patch.label !== undefined &&
            merged.kind === "check" &&
            NUMERIC_PATTERN.test(merged.label || "")
          ) {
            merged.kind = "number";
          }
          return merged;
        }),
      };
    }));

  // Build a sanitised body identical to what the server will receive on
  // publish. Used by both `saveDraftNow()` and `publishNow()`.
  //
  // Backend contract (verified against prod `/admin/inspection-templates`):
  //   • sections[i].name       (NOT "title" — the earlier code sent `title`
  //                             and the API returned 422 "sections.0.name
  //                             Field required", making Publish silently
  //                             appear to do nothing.)
  //   • sections[i].intro      (optional description under the section head)
  //   • applies_to             — ARRAY of asset-type strings (a single
  //                             string 422s the same way).
  const buildBody = () => {
    if (!name.trim()) return { err: "Name is required." };
    const cleaned = sections
      .map((sec) => ({ ...sec, items: sec.items.filter((it) => (it.label || "").trim()) }))
      .filter((sec) => (sec.title || sec.name || "").trim() && sec.items.length);
    if (!cleaned.length) return { err: "Add at least one section with an item." };
    // Drop options[] from non-select kinds to keep the payload tight.
    const finalSections = cleaned.map((sec) => ({
      name: (sec.title || sec.name || "").trim(),
      intro: sec.intro || undefined,
      items: sec.items.map((it) => {
        const base = { label: it.label, kind: it.kind, required: !!it.required };
        if (it.kind === "select" && Array.isArray(it.options) && it.options.length) {
          base.options = it.options.filter((o) => String(o).trim());
        }
        if (it.unit) base.unit = it.unit;
        return base;
      }),
    }));
    // Machine type also fills `applies_to` so the mobile app's asset-scope
    // filter locks the template to the right category. Accept either a
    // hand-typed comma-separated string OR an array (AI generation returns
    // `applies_to: ["LV"]`), so Publish doesn't crash on the .split() call.
    let appliesArr;
    if (Array.isArray(appliesTo)) {
      appliesArr = appliesTo.map((s) => String(s || "").trim()).filter(Boolean);
    } else {
      appliesArr = String(appliesTo || machineType || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return {
      body: {
        name: name.trim(),
        machine_type: machineType.trim() || null,
        cadence: cadence || null,
        description: description.trim() || null,
        applies_to: appliesArr.length ? appliesArr : null,
        require_smu: requireSmu,
        sections: finalSections,
      },
    };
  };

  const saveDraftNow = () => {
    setMsg(null);
    const { err, body } = buildBody();
    if (err) { setMsg({ type: "err", text: err }); return; }
    const stored = saveDraft({
      draft_id: draftId,
      source_template_id: templateId,
      ...body,
    });
    setMsg({ type: "ok", text: `Draft saved locally · ${new Date(stored.saved_at).toLocaleTimeString()}` });
    setTimeout(() => onSaved?.({ kind: "draft" }), 800);
  };

  const publishNow = async () => {
    setMsg(null);
    const { err, body } = buildBody();
    if (err) { setMsg({ type: "err", text: err }); return; }
    // Josh (Feb 26): "cant sumit them at the bottom for some reason" — the
    // native window.confirm() was blocked in some environments (embedded
    // preview iframe, some strict browser configs) which made Publish appear
    // to do nothing at all. Swapped to an in-app confirm modal so the flow
    // is fully visible and controllable.
    setShowPublishConfirm(true);
  };

  const doPublish = async () => {
    setShowPublishConfirm(false);
    const { err, body } = buildBody();
    if (err) { setMsg({ type: "err", text: err }); return; }
    const publishedBy = user?.name || user?.username || "Owner";
    const publishedAt = new Date().toISOString();
    // Stamp version metadata into the description so it's visible to every
    // client — even the mobile app, which has no versioning code.
    const finalBody = {
      ...body,
      description: attachVersionStamp(body.description || "", {
        version: nextV, publishedBy, publishedAt,
      }),
    };
    setBusy(true);
    try {
      let saved;
      if (isEdit && templateId) {
        const { data } = await api.patch(`/admin/inspection-templates/${templateId}`, finalBody);
        saved = data || finalBody;
      } else {
        const { data } = await api.post("/admin/inspection-templates", finalBody);
        saved = data || finalBody;
      }
      recordPublish({
        templateId: saved?.id || saved?._id || templateId || `local_${Date.now()}`,
        version: nextV,
        publishedBy,
        name: body.name,
        sections: body.sections,
      });
      // If this originated as a draft, clean it up on successful publish.
      if (draftId) deleteDraft(draftId);
      setMsg({ type: "ok", text: `Published v${nextV} · every tablet on this site will pick it up on next sync.` });
      setTimeout(() => onSaved?.({ kind: "publish", version: nextV }), 900);
    } catch (ex) {
      setBusy(false);
      setMsg({ type: "err", text: apiError(ex) });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ib-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center gap-3 p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <PencilRuler className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-2 flex-wrap">
              {isDraft ? "Draft · unpublished" : (isEdit ? "Edit template" : "New template")}
              {currentVersion?.version && (
                <span
                  className="pill !text-[10px] !py-0.5 !px-2 font-black uppercase tracking-widest"
                  style={{ background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)" }}
                  data-testid="ib-current-version"
                  title={`Published by ${currentVersion.published_by || "—"} on ${currentVersion.published_at || "—"}`}
                >
                  Live · v{currentVersion.version}
                </span>
              )}
              {isEdit && (
                <span
                  className="pill !text-[10px] !py-0.5 !px-2 font-black uppercase tracking-widest"
                  style={{ background: "rgba(245,130,32,0.12)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}
                  data-testid="ib-next-version"
                >
                  Next publish → v{nextV}
                </span>
              )}
            </div>
            <div className="font-display font-black text-xl tracking-tight">Inspection Builder</div>
          </div>
          {versionRows.length > 0 && (
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => setShowHistory(true)}
              data-testid="ib-history-open"
              title="View publish history"
            >
              <History className="w-4 h-4" /> History
            </button>
          )}
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose} data-testid="ib-close"><XIcon className="w-4 h-4" /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="field md:col-span-2">
              <label>Template name *</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Daily digger prestart" data-testid="ib-name" />
            </div>
            <div className="field">
              <label>Machine type / category</label>
              <select
                className="input"
                value={
                  // Show as "custom" when the current value isn't in the
                  // known list — that keeps free-text still workable.
                  machineType && !assetTypes.includes(machineType) ? "__custom__" : machineType
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "__custom__") {
                    // Prompt-driven custom type — kept simple. When users
                    // add a new asset type via the Fleet page it'll appear
                    // in this list automatically on next open.
                    const custom = window.prompt("Custom machine type (e.g. EX3600, LV, Water Cart):", machineType || "");
                    if (custom !== null) setMachineType(custom.trim());
                  } else {
                    setMachineType(v);
                  }
                }}
                data-testid="ib-machine-type"
              >
                <option value="">— Any machine —</option>
                {assetTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
                {machineType && !assetTypes.includes(machineType) && (
                  <option value={machineType}>{machineType}</option>
                )}
                <option value="__custom__">+ Custom type…</option>
              </select>
              {machineType && (
                <div className="text-[10px] text-muted mt-1 font-mono-tight">
                  Locked to: <span className="text-white">{machineType}</span>
                </div>
              )}
            </div>
            <div className="field">
              <label>Cadence</label>
              <select className="input" value={cadence} onChange={(e) => setCadence(e.target.value)} data-testid="ib-cadence">
                <option value="">— None —</option>
                {CADENCES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="field md:col-span-2">
              <label>Description</label>
              <textarea className="input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this template is for" data-testid="ib-desc" />
            </div>
            <div className="field">
              <label>Applies to</label>
              <input className="input" value={appliesTo} onChange={(e) => setAppliesTo(e.target.value)} placeholder="specific machine ID or fleet" data-testid="ib-applies-to" />
            </div>
            <div className="field flex items-end">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={requireSmu}
                  onChange={(e) => setRequireSmu(e.target.checked)}
                  data-testid="ib-require-smu"
                />
                Require SMU (service meter) at start
              </label>
            </div>
          </div>

          <div className="space-y-4">
            {sections.map((sec, si) => (
              <div key={si} className="panel p-4" data-testid={`ib-section-${si}`}>
                <div className="flex items-center gap-2 mb-3">
                  <GripVertical className="w-4 h-4 text-muted" />
                  <input
                    className="input flex-1 !py-2 !text-sm font-display font-black tracking-wider"
                    value={sec.title}
                    onChange={(e) => updateSection(si, { title: e.target.value })}
                    placeholder="Section title"
                    data-testid={`ib-section-title-${si}`}
                  />
                  <button className="btn btn-sm btn-ghost text-red-400" onClick={() => removeSection(si)} title="Remove section">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3">
                  {sec.items.map((it, ii) => {
                    const kindMeta = ITEM_KINDS.find((k) => k.id === it.kind) || ITEM_KINDS[0];
                    return (
                      <div key={ii} className="space-y-1.5" data-testid={`ib-item-${si}-${ii}`}>
                        <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
                          <input
                            className="input flex-1 !py-2 !text-sm min-w-0"
                            value={it.label}
                            onChange={(e) => updateItem(si, ii, { label: e.target.value })}
                            placeholder="Item label · e.g. SMU reading, Track tension, Oil level…"
                            data-testid={`ib-item-label-${si}-${ii}`}
                          />
                          <select
                            className="input !py-2 !text-sm !w-64"
                            value={it.kind}
                            onChange={(e) => updateItem(si, ii, { kind: e.target.value })}
                            data-testid={`ib-item-kind-${si}-${ii}`}
                            title="How the tradesman will answer this item on the tablet"
                          >
                            {ITEM_KINDS.map((k) => (<option key={k.id} value={k.id}>{k.label}</option>))}
                          </select>
                          <label className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-muted whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={!!it.required}
                              onChange={(e) => updateItem(si, ii, { required: e.target.checked })}
                            />
                            Required
                          </label>
                          <button className="btn btn-sm btn-ghost text-red-400" onClick={() => removeItem(si, ii)} title="Remove">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Helper hint — shows what the mobile app will draw for this
                            kind so Josh isn't guessing. Also acts as a nudge when
                            he picks Numeric for a field like SMU. */}
                        <div className="text-[11px] text-muted font-mono-tight pl-2 pr-2">
                          <span className="text-white/70 font-black uppercase tracking-widest">On tablet →</span>{" "}
                          {kindMeta.hint}
                        </div>
                        {/* Options editor — visible only for dropdown items.
                            One option per line so the Owner can type freely
                            without opening a chip builder. */}
                        {it.kind === "select" && (
                          <div className="pl-2 pr-2" data-testid={`ib-item-options-${si}-${ii}`}>
                            <label className="text-[10px] uppercase tracking-widest text-muted font-black">Dropdown options · one per line *</label>
                            <textarea
                              className="input !py-2 !text-sm mt-1 font-mono-tight"
                              rows={Math.min(6, Math.max(3, (it.options || []).length + 1))}
                              value={(it.options || []).join("\n")}
                              placeholder={"e.g.\nLow\nMedium\nHigh\nCritical"}
                              onChange={(e) => updateItem(si, ii, {
                                options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                              })}
                            />
                            <div className="text-[10px] text-muted mt-1">
                              {(it.options || []).length} option{(it.options || []).length === 1 ? "" : "s"} · tablet renders as a pill picker.
                            </div>
                          </div>
                        )}
                        {/* Numeric unit — helps the mobile app render "165 mm"
                            or "5678 hrs" instead of a bare number. */}
                        {it.kind === "number" && (
                          <div className="pl-2 pr-2 flex items-center gap-2" data-testid={`ib-item-unit-${si}-${ii}`}>
                            <label className="text-[10px] uppercase tracking-widest text-muted font-black">Unit (optional)</label>
                            <input
                              className="input !py-1.5 !text-sm !w-32"
                              value={it.unit || ""}
                              placeholder="hrs · psi · mm · °C"
                              onChange={(e) => updateItem(si, ii, { unit: e.target.value })}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <button className="btn btn-sm btn-ghost mt-1" onClick={() => addItem(si)} data-testid={`ib-add-item-${si}`}>
                    <Plus className="w-4 h-4" /> Add item
                  </button>
                </div>
              </div>
            ))}
            <button className="btn btn-ghost w-full justify-center" onClick={addSection} data-testid="ib-add-section">
              <Plus className="w-4 h-4" /> Add section
            </button>
          </div>

          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`} data-testid="ib-msg">
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}
        </div>

        <div className="p-4 flex items-center justify-between gap-3 flex-wrap" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="text-xs text-muted">
            {sections.length} section{sections.length === 1 ? "" : "s"} ·{" "}
            {sections.reduce((n, s) => n + s.items.length, 0)} item
            {sections.reduce((n, s) => n + s.items.length, 0) === 1 ? "" : "s"}
            {" · "}
            <span className="text-white/70">Historical reports are version-locked · this publish only affects new inspections.</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setPreviewing(true)}
              data-testid="ib-preview"
              title="See how this template looks on a field tablet"
            >
              <Eye className="w-4 h-4" /> Preview
            </button>
            <button
              className="btn btn-sm"
              onClick={saveDraftNow}
              disabled={busy}
              data-testid="ib-save-draft"
              title="Save locally · does NOT reach the field crew"
            >
              <FileEdit className="w-4 h-4" /> Save Draft
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={publishNow}
              disabled={busy}
              data-testid="ib-publish"
              title="Push this template live — every tablet syncs it within seconds"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
              Publish v{nextV}
            </button>
          </div>
        </div>
      </div>
      {previewing && (
        <TemplatePreviewModal
          name={name}
          machineType={machineType}
          cadence={cadence}
          requireSmu={requireSmu}
          sections={sections}
          nextVersion={nextV}
          busy={busy}
          onClose={() => setPreviewing(false)}
          onPublish={() => { setPreviewing(false); publishNow(); }}
        />
      )}
      {showHistory && (
        <VersionHistoryModal
          templateId={templateId}
          onClose={() => setShowHistory(false)}
        />
      )}
      {showPublishConfirm && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
          onClick={() => !busy && setShowPublishConfirm(false)}
          data-testid="ib-publish-confirm"
        >
          <div className="panel-raised w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "rgba(245,130,32,0.18)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.4)" }}
              >
                <Rocket className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-muted">Publish inspection template</div>
                <div className="font-display font-black text-xl leading-tight mt-1">
                  Publish v{nextV} of &quot;{name || "template"}&quot; now?
                </div>
                <div className="text-sm text-white/80 mt-3 leading-relaxed">
                  This replaces the live template every tablet uses across the site. Historical reports stay locked to whichever version they were signed on — nothing already filed will change.
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowPublishConfirm(false)}
                disabled={busy}
                data-testid="ib-publish-cancel"
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={doPublish}
                disabled={busy}
                data-testid="ib-publish-confirm-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                Publish v{nextV}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Expert (AI) Builder ---------------- */
function ExpertBuilderModal({ onClose, onGenerated }) {
  const [instruction, setInstruction] = useState("");
  const [context, setContext] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const generate = async () => {
    if (!instruction.trim() && !file) {
      setMsg({ type: "err", text: "Give an instruction or upload a PDF/photo." }); return;
    }
    setBusy(true); setMsg(null);
    try {
      let tpl;
      // Backend expects `context` as a DICT, not a string. Wrap the user's
      // free-text reference into `{ note: "…" }` so /admin/builder/generate
      // stops 422-ing with "Input should be a valid dictionary". Josh
      // (Feb 27): "clicking 'GENERATE' does nothing" — this was the culprit
      // whenever the context field had any text in it.
      const ctxObj = context.trim() ? { note: context.trim() } : undefined;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        if (instruction.trim()) fd.append("instruction", instruction.trim());
        // Multipart wants a JSON string for the context part.
        if (ctxObj) fd.append("context", JSON.stringify(ctxObj));
        const { data } = await api.post("/admin/builder/generate-from-file", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        tpl = data?.template || data;
      } else {
        const body = { instruction: instruction.trim() };
        if (ctxObj) body.context = ctxObj;
        const { data } = await api.post("/admin/builder/generate", body);
        tpl = data?.template || data;
      }
      if (!tpl || !tpl.sections) throw new Error("The builder returned an unexpected shape.");
      setMsg({ type: "ok", text: "Draft generated — opening editor…" });
      setTimeout(() => onGenerated(tpl), 400);
    } catch (ex) {
      setBusy(false);
      setMsg({ type: "err", text: apiError(ex) });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ib-expert-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted">Expert Builder</div>
            <div className="font-display font-black text-lg tracking-tight">Generate inspection from a prompt or a PDF</div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose}><XIcon className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <div className="field">
            <label>Instruction</label>
            <textarea
              className="input"
              rows={4}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="Weekly Hitachi digger prestart. Include GET wear, hydraulics, cabin, undercarriage, and a supervisor signature at the end."
              data-testid="ib-expert-instruction"
            />
          </div>
          <div className="field">
            <label>Context (optional)</label>
            <input
              className="input"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="e.g. Site standard is TAS-2023"
              data-testid="ib-expert-context"
            />
          </div>
          <div className="field">
            <label className="flex items-center gap-2 cursor-pointer">
              <Upload className="w-4 h-4" /> Reference file (optional · PDF / photo)
            </label>
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm"
              data-testid="ib-expert-file"
            />
            {file && <div className="text-[11px] text-muted mt-1 font-mono-tight">{file.name}</div>}
          </div>

          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`}>
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={generate} disabled={busy} data-testid="ib-expert-generate">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Generate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Same numeric-sniff regex used in the live editor's updateItem hook. Keeping
// a single source of truth so both AI-imported templates and manual edits
// resolve to the same kind for the same label.
const NUMERIC_PATTERN_RE = /\b(smu|s\.m\.u|meter|reading|hours?|hrs?|pressure|temperature|temp|psi|bar|kpa|kwh|amps?|volts?|litres?|liters?|kg|degrees?|deg|rpm|torque|%|percent)\b/i;

function normSection(s) {
  return {
    title: s.title || s.name || "Section",
    items: (s.items || []).map((it) => {
      const rawKind = it.kind || it.type || "check";
      const label = it.label || it.title || it.name || "";
      // If the AI-generated template stamps `check` on an item whose label
      // is clearly a reading (SMU, hours, pressure, %), promote it to
      // `number` on load. Josh explicitly called this out: "change all day
      // it says SMU but that needs to be numbers not okay and that".
      const kind = (rawKind === "check" && NUMERIC_PATTERN_RE.test(label)) ? "number" : rawKind;
      const out = {
        label,
        kind,
        required: it.required ?? false,
      };
      // Preserve options for select/dropdown kinds. Accepts either an array
      // of strings or an array of `{ value, label }` — normalises to strings.
      if (kind === "select" && Array.isArray(it.options)) {
        out.options = it.options
          .map((o) => (typeof o === "string" ? o : (o?.label || o?.value || "")))
          .map((s) => String(s).trim())
          .filter(Boolean);
      }
      // Preserve unit for numeric measurements so SMU carries "hrs" through.
      if (it.unit) out.unit = String(it.unit);
      return out;
    }),
  };
}


/* ---------------- Preview modal (tablet-parity render) ---------------- */
function TemplatePreviewModal({ name, machineType, cadence, requireSmu, sections, nextVersion, busy, onClose, onPublish }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="ib-preview-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center gap-3 p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(139,180,255,0.15)", color: "#8bb4ff", border: "1px solid rgba(139,180,255,0.3)" }}>
            <Eye className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Tablet Preview · read-only</div>
            <div className="font-display font-black text-lg tracking-tight truncate">{name || "Untitled template"}</div>
            <div className="text-[11px] text-muted font-mono-tight mt-0.5">
              {machineType || "any machine"}{cadence ? ` · ${cadence}` : ""} · would publish as <span style={{ color: "var(--primary)" }}>v{nextVersion}</span>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose} data-testid="ib-preview-close"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {requireSmu && (
            <div className="panel-accent p-3">
              <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: "var(--primary)" }}>SMU required at start</div>
              <div className="text-[13px] text-white/80 mt-0.5">Tradesman must enter the machine&apos;s hour meter before answering any item.</div>
            </div>
          )}
          {sections.map((sec, si) => (
            <div key={si} className="panel p-4" data-testid={`ib-preview-section-${si}`}>
              <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: "var(--primary)" }}>
                Section {si + 1}
              </div>
              <div className="font-display font-black text-base mt-0.5">{sec.title || "Section"}</div>
              <div className="mt-3 space-y-2">
                {sec.items.map((it, ii) => <PreviewItem key={ii} item={it} />)}
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 flex items-center justify-end gap-2 flex-wrap" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="text-[11px] text-muted flex-1 min-w-[180px]">
            Publish makes this the live template every tablet uses on the next sync.
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            data-testid="ib-preview-back"
          >
            <ArrowLeft className="w-4 h-4" /> Keep editing
          </button>
          {onPublish && (
            <button
              className="btn btn-primary btn-sm"
              onClick={onPublish}
              disabled={busy}
              data-testid="ib-preview-publish"
              title="Push this template live"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
              Publish v{nextVersion}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewItem({ item }) {
  const kind = item.kind || "check";
  return (
    <div className="rounded-lg px-3 py-2.5" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-white/95">{item.label || "—"}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted mt-0.5 flex items-center gap-2">
            <span>{kindLabel(kind)}</span>
            {item.required && <span className="text-red-400">Required</span>}
            {item.unit && <span>· unit {item.unit}</span>}
          </div>
        </div>
        <PreviewInput kind={kind} options={item.options} unit={item.unit} />
      </div>
    </div>
  );
}
function PreviewInput({ kind, options, unit }) {
  if (kind === "check") return (
    <div className="flex gap-1">
      <span className="pill pill-green !text-[10px] !py-0.5 !px-2">OK</span>
      <span className="pill pill-orange !text-[10px] !py-0.5 !px-2">Issue</span>
      <span className="pill !text-[10px] !py-0.5 !px-2">N/A</span>
    </div>
  );
  if (kind === "number") return (
    <input disabled placeholder={`0${unit ? " " + unit : ""}`} className="input !py-1 !text-sm !w-28 tabular-nums text-right" />
  );
  if (kind === "text") return <input disabled placeholder="short note" className="input !py-1 !text-sm !w-40" />;
  if (kind === "photo") return <span className="pill pill-orange-solid !text-[10px] !py-0.5 !px-2">📷 Capture</span>;
  if (kind === "select") return (
    <div className="flex gap-1 flex-wrap justify-end">
      {(options || []).slice(0, 4).map((o, i) => (
        <span key={i} className="pill !text-[10px] !py-0.5 !px-2" style={{ background: "rgba(139,180,255,0.12)", color: "#8bb4ff", border: "1px solid rgba(139,180,255,0.3)" }}>{o}</span>
      ))}
      {(options || []).length > 4 && <span className="text-[10px] text-muted">+{options.length - 4}</span>}
      {(!options || !options.length) && <span className="text-[10px] text-red-400">no options set</span>}
    </div>
  );
  if (kind === "percent") return <span className="pill !text-[10px] !py-0.5 !px-2">0–100 %</span>;
  if (kind === "rating") return <span className="pill !text-[10px] !py-0.5 !px-2">★★★☆☆</span>;
  if (kind === "signature") return <span className="pill !text-[10px] !py-0.5 !px-2">✎ Sign</span>;
  return <span className="pill !text-[10px] !py-0.5 !px-2">{kind}</span>;
}
function kindLabel(kind) {
  const m = ITEM_KINDS.find((k) => k.id === kind);
  return m ? m.label : kind;
}

/* ---------------- Version-history modal (localStorage log) ---------------- */
function VersionHistoryModal({ templateId, onClose }) {
  const rows = listVersions(templateId).slice().sort((a, b) => (b.version || 0) - (a.version || 0));
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="ib-history-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-xl max-h-[80vh] flex flex-col">
        <div className="flex items-center gap-3 p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)" }}>
            <History className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Publish History</div>
            <div className="font-display font-black text-lg tracking-tight">{rows.length} release{rows.length === 1 ? "" : "s"} logged</div>
          </div>
          <button className="btn btn-sm btn-ghost !px-2" onClick={onClose} data-testid="ib-history-close"><XIcon className="w-4 h-4" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-2">
          {rows.length === 0 ? (
            <div className="text-muted text-sm">No publishes logged on this device yet.</div>
          ) : rows.map((r) => (
            <div key={`${r.template_id}-${r.version}-${r.published_at}`} className="panel p-3 flex items-center gap-3" data-testid={`ib-history-row-v${r.version}`}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center font-black tabular-nums"
                   style={{ background: "rgba(245,130,32,0.12)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
                v{r.version}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-black text-white truncate">{r.name || "—"}</div>
                <div className="text-[11px] text-muted font-mono-tight">
                  {r.published_by} · {new Date(r.published_at).toLocaleString()} · {r.section_count} sec · {r.item_count} items
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
